import { activityMessageSchema } from "@memo-air/protocol";
import { clientFetch } from "@app/lib/egress/client";
import { useLiveDocs } from "@app/lib/swr/live_docs";
import type { LiveDocSummaryType, LiveDocType } from "@app/types/live_docs";
import type { LightWorkspaceType } from "@app/types/user";
import { HocuspocusProvider } from "@hocuspocus/provider";
import { useEffect, useMemo, useSyncExternalStore } from "react";
import * as Y from "yjs";

// One watch connection per page, shared by every card, Library row and Home section, so status
// lines update the moment live-server pushes, without polling and without opening each doc.
type WatchStore = {
  workspaceId: string;
  docKey: string;
  provider: HocuspocusProvider | null;
  pushed: Record<string, LiveDocSummaryType>;
  users: number;
};

let store: WatchStore | null = null;
const listeners = new Set<() => void>();

function notify() {
  for (const listener of listeners) {
    listener();
  }
}

async function fetchWatchToken(
  workspaceId: string,
  watchId: string
): Promise<{ url: string; token: string } | null> {
  const res = await clientFetch(
    `/api/w/${workspaceId}/live-docs/watch-token?watchId=${watchId}`
  );
  return res.ok ? res.json() : null;
}

function connect(current: WatchStore) {
  current.provider?.destroy();
  current.provider = null;
  const watchId = crypto.randomUUID();
  const { workspaceId, docKey } = current;
  void fetchWatchToken(workspaceId, watchId).then((watch) => {
    // A newer doc list or the last user leaving replaced this store while the token was in flight.
    if (!watch || store !== current || current.docKey !== docKey) {
      return;
    }
    current.provider = new HocuspocusProvider({
      url: watch.url,
      name: `watch:${watchId}`,
      document: new Y.Doc(),
      // Reconnects re-mint, so the watched list follows what the user may see now.
      token: async () =>
        (await fetchWatchToken(workspaceId, watchId))?.token ?? "",
      onStateless: ({ payload }) => {
        const message = activityMessageSchema.safeParse(JSON.parse(payload));
        if (!message.success) {
          return;
        }
        const { docName, ...summary } = message.data.summary;
        current.pushed = { ...current.pushed, [docName]: summary };
        notify();
      },
    });
  });
}

function acquire(workspaceId: string, docKey: string): WatchStore {
  if (!store || store.workspaceId !== workspaceId) {
    store?.provider?.destroy();
    store = {
      workspaceId,
      docKey: "",
      provider: null,
      pushed: {},
      users: 0,
    };
  }
  store.users += 1;
  if (docKey && store.docKey !== docKey) {
    store.docKey = docKey;
    connect(store);
  }
  return store;
}

function release(current: WatchStore) {
  current.users -= 1;
  if (current.users === 0 && store === current) {
    current.provider?.destroy();
    store = null;
    notify();
  }
}

const emptyPushed: Record<string, LiveDocSummaryType> = {};

export function useLiveDocActivity({
  owner,
  disabled,
}: {
  owner: LightWorkspaceType;
  disabled?: boolean;
}): {
  liveDocs: LiveDocType[];
  summaries: Record<string, LiveDocSummaryType>;
} {
  const { liveDocs, summaries } = useLiveDocs({ owner, disabled });
  const docKey = useMemo(
    () =>
      liveDocs
        .map((doc) => doc.sId)
        .sort()
        .join(","),
    [liveDocs]
  );

  useEffect(() => {
    if (disabled || !docKey) {
      return;
    }
    const current = acquire(owner.sId, docKey);
    return () => release(current);
  }, [owner.sId, docKey, disabled]);

  const pushed = useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => (store?.workspaceId === owner.sId ? store.pushed : emptyPushed)
  );

  return useMemo(
    () => ({ liveDocs, summaries: { ...summaries, ...pushed } }),
    [liveDocs, summaries, pushed]
  );
}
